import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import React, {
	forwardRef,
	type HTMLProps,
	type ReactNode,
	type Ref,
} from 'react';
import { Color } from '../../../features/shared/styles/Color';
import ExternalLinkIcon from '../../new-assets/external-link.svg';
import type { ITheme } from '../../themer';
import themed from '../../themer/themed';
import { useConfig } from '../Config';
import { VisuallyHidden } from '../VisuallyHidden';
import { isHashFragment } from './isHashFragment';
import { isRelativeUrl } from './isRelativeUrl';

export interface IStyledLinkProps {
	inverted?: boolean;
	customColor?: string;
}

export interface ILinkProps
	extends HTMLProps<HTMLAnchorElement>,
		IStyledLinkProps {
	children: ReactNode;
	ref?: Ref<HTMLAnchorElement>;
	hideExternalIndicator?: boolean;
	inverted?: boolean;
	screenReaderTitle?: string;
	forceRefresh?: boolean;
}

export const BaseLink = forwardRef<HTMLAnchorElement, ILinkProps>(
	(
		{
			children,
			href,
			inverted,
			hideExternalIndicator,
			screenReaderTitle,
			forceRefresh,
			...rest
		}: ILinkProps,
		ref,
	) => {
		const { RouterLinkComponent, translations } = useConfig();

		return RouterLinkComponent &&
			href &&
			shouldUseRouterLink(href) &&
			!forceRefresh ? (
			<RouterLinkComponent href={href} ref={ref} {...rest}>
				{children}
			</RouterLinkComponent>
		) : (
			<a href={href} ref={ref} {...rest}>
				<span style={{ display: 'inline-flex', alignItems: 'center' }}>
					{!screenReaderTitle ? (
						children
					) : (
						<>
							<span aria-hidden>{children}</span>
							<VisuallyHidden>{screenReaderTitle}</VisuallyHidden>
						</>
					)}
					{rest.target === '_blank' && !hideExternalIndicator && (
						<>
							<ExternalLinkIcon aria-hidden />
							<VisuallyHidden as="span">
								{translations.externalLinkLabel}
							</VisuallyHidden>
						</>
					)}
				</span>
			</a>
		);
	},
);

const shouldUseRouterLink = (href: string) =>
	!isHashFragment(href) && isRelativeUrl(href);

export const Link = styled(BaseLink, {
	shouldForwardProp: (prop) =>
		prop === 'getProps' ||
		prop === 'inverted' ||
		isPropValid(prop) ||
		prop === 'hideExternalIndicator' ||
		prop === 'forceRefresh',
})<IStyledLinkProps, ITheme>`
	text-decoration: underline !important;
	color: ${themed<IStyledLinkProps>(({ typography, color }, { inverted }) =>
		inverted ? color.text.inverted : typography.link.color,
	)};
	padding: 5px 8px;
	border-radius: 4px;

	&:hover {
		color: ${Color.TextWhite};
		background-color: ${Color.PrimaryDefault};
	}

	&:focus-visible {
		color: ${Color.TextWhite};
		background-color: ${Color.PrimaryDefault};
		outline: 2px solid ${Color.Highlight};
	}
`;
